import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Divider, Typography } from '@mui/material';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import {
  format,
  addDays,
  addHours,
  addMonths,
  setHours,
  setMinutes,
  isToday,
  isTomorrow,
  isPast,
  isSameDay,
  startOfDay,
  endOfDay,
  getMinutes,
  getHours,
  getDate,
  getMonth,
  getYear,
  parse,
  differenceInMinutes,
  isAfter,
  isBefore
} from 'date-fns';
import { BUTTON_PRIMARY, BUTTON_SECONDARY, BUTTON_WARNING, TOAST_ERROR } from '../assets/constants/Constants';
import CustomDialog from './CustomDialog';
import ShowToast from './ToastComponent';
import CircularProgressLoader from './CircularProgressLoader';

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const MAX_BOOKING_WINDOW_MONTHS = 6;

// Helper function to get next 30-min slot
const getNextHalfHourSlot = (date) => {
  const minutes = getMinutes(date);
  const remainder = 30 - (minutes % 30);
  return addMinutes(date, remainder);
};

// Helper to generate time slots based on selected date (full day from 12:00 AM to 11:30 PM)
const LAST_TIME_SLOT = '11:30 PM';

const ensureLastTimeSlot = (slots) => {
  if (!slots.includes(LAST_TIME_SLOT)) {
    slots.push(LAST_TIME_SLOT);
  }

  return slots;
};

const generateTimeSlots = (selectedDate, fromDate = null, maxDateTime = null) => {
  if (!selectedDate) return [];
  
  const slots = [];
  let startTime;
  
  if (isToday(selectedDate)) {
    // For today: start from next half hour slot
    startTime = getNextHalfHourSlot(fromDate || new Date());
    
    // Generate slots from startTime until 11:30 PM
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 30, 0, 0);
    
    let currentSlot = new Date(startTime);
    while (currentSlot <= endOfDay) {
      if (!maxDateTime || currentSlot <= maxDateTime) {
        slots.push(format(currentSlot, 'h:mm a'));
      }
      currentSlot = addMinutes(currentSlot, 30);
    }
  } else {
    // For tomorrow or custom date: start from 12:00 AM and go through entire day
    startTime = startOfDay(selectedDate);
    const endOfDay = new Date(selectedDate);
    endOfDay.setHours(23, 30, 0, 0);
    
    let currentSlot = new Date(startTime);
    while (currentSlot <= endOfDay) {
      if (!maxDateTime || currentSlot <= maxDateTime) {
        slots.push(format(currentSlot, 'h:mm a'));
      }
      currentSlot = addMinutes(currentSlot, 30);
    }
  }
  
  // If no slots available (all times past), show next day's slots
  if (slots.length === 0) {
    const nextDay = addDays(selectedDate, 1);
    if (maxDateTime && isAfter(startOfDay(nextDay), maxDateTime)) {
      return [];
    }
    const nextDayStart = startOfDay(nextDay);
    const nextDayEnd = new Date(nextDay);
    nextDayEnd.setHours(23, 30, 0, 0);
    
    let currentSlot = new Date(nextDayStart);
    while (currentSlot <= nextDayEnd) {
      if (!maxDateTime || currentSlot <= maxDateTime) {
        slots.push(format(currentSlot, 'h:mm a'));
      }
      currentSlot = addMinutes(currentSlot, 30);
    }
  }
  
  return maxDateTime ? slots : ensureLastTimeSlot(slots);
};

// Helper to add minutes to a date
const addMinutes = (date, minutes) => {
  return new Date(date.getTime() + minutes * 60000);
};

// Helper to format date to MM/dd/yyyy
const formatDateToString = (date) => {
  if (!date) return "";
  return format(date, 'M/d/yyyy');
};

// Helper to format date to ISO string with time
const formatToISOString = (date, timeStr) => {
  if (!date || !timeStr) return "";
  
  try {
    // Parse the time string (e.g., "2:30 PM")
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 24-hour format
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    // Create new date with the time
    const dateTime = new Date(date);
    dateTime.setHours(hours, minutes, 0, 0);
    
    // Return ISO string
    return dateTime.toISOString();
  } catch (error) {
    console.error('Error formatting to ISO:', error);
    return "";
  }
};

// Helper to add 24 hours to a date
const add24Hours = (date) => {
  if (!date) return null;
  return addHours(date, 24);
};

// Helper to parse time string to Date object
const parseTimeToDate = (date, timeStr) => {
  if (!date || !timeStr) return null;
  
  try {
    const [time, period] = timeStr.split(' ');
    let [hours, minutes] = time.split(':').map(Number);
    
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    
    return setMinutes(setHours(new Date(date), hours), minutes);
  } catch (error) {
    console.error('Error parsing time:', error);
    return null;
  }
};

function Calendar({ onSelect, onClose, minDate = new Date(), maxDate = null, selectedDate }) {
  const today = new Date();
  const [viewDate, setViewDate] = useState(selectedDate || today);
  const [picked, setPicked] = useState(null);

  const viewYear = getYear(viewDate);
  const viewMonth = getMonth(viewDate);
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const minBoundary = startOfDay(minDate);
  const maxBoundary = maxDate ? endOfDay(maxDate) : null;

  const isMonthBeforeMin = () => {
    const previousMonthLastDate = new Date(viewYear, viewMonth, 0);
    return previousMonthLastDate < minBoundary;
  };

  const isMonthAfterMax = () => {
    if (!maxBoundary) return false;
    const nextMonthFirstDate = new Date(viewYear, viewMonth + 1, 1);
    return nextMonthFirstDate > maxBoundary;
  };

  const prevMonth = () => {
    if (isMonthBeforeMin()) return;
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    setPicked(null);
  };

  const nextMonth = () => {
    if (isMonthAfterMax()) return;
    setViewDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    setPicked(null);
  };

  const isDatePast = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    const isBeforeMinDate = date < minBoundary && !isSameDay(date, minBoundary);
    const isAfterMaxDate = maxBoundary ? date > maxBoundary && !isSameDay(date, maxBoundary) : false;
    return isBeforeMinDate || isAfterMaxDate;
  };

  const isDateToday = (day) => {
    const date = new Date(viewYear, viewMonth, day);
    return isSameDay(date, today);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const handleConfirm = () => {
    if (!picked) return;
    const selectedDate = new Date(viewYear, viewMonth, picked);
    onSelect(selectedDate, format(selectedDate, 'MMM d'));
    onClose();
  };

  return (
    <div className="calendar-card">
        <div className="calendar-header">
          <button className="calendar-nav" onClick={prevMonth}>‹</button>
          <span className="calendar-month">{MONTHS[viewMonth]} {viewYear}</span>
          <button className="calendar-nav" onClick={nextMonth}>›</button>
        </div>

        <div className="calendar-weekdays">
          {WEEKDAYS.map(d => (
            <div key={d} className="calendar-weekday">{d}</div>
          ))}
        </div>

        <div className="calendar-days">
          {cells.map((day, i) => {
            if (!day) {
              return <div key={`e-${i}`} className="calendar-day empty" />;
            }
            
            const past = isDatePast(day);
            const sel = picked === day;
            const tod = isDateToday(day);
            
            let cls = "calendar-day";
            if (past) cls += " past";
            else if (sel) cls += " selected";
            else if (tod) cls += " today";
            
            return (
              <button 
                key={day} 
                className={cls} 
                onClick={() => !past && setPicked(day)}
                disabled={past}
              >
                {day}
              </button>
            );
          })}
        </div>

        <button 
          className="calendar-confirm" 
          onClick={handleConfirm} 
          disabled={!picked}
        >
          {picked
            ? `Confirm — ${MONTHS[viewMonth].slice(0, 3)} ${picked}`
            : "Select a day"}
        </button>
    </div>
  );
}

const DateTimeSelector = ({ onContinue }) => {
  const { t } = useTranslation();
  const isMountedRef = useRef(true);
  const [activeSection, setActiveSection] = useState("dropoff");
  const [bookingWindowStart] = useState(() => new Date());
  const [bookingWindowEnd] = useState(() => addMonths(new Date(), MAX_BOOKING_WINDOW_MONTHS));
  
  // Dropoff state
  const [dropoffDate, setDropoffDate] = useState(new Date());
  const [dropoffTime, setDropoffTime] = useState("");
  const [dropoffCustomLabel, setDropoffCustomLabel] = useState(null);
  const [showDropoffCalendar, setShowDropoffCalendar] = useState(false);
  const [dropoffTimeSlots, setDropoffTimeSlots] = useState([]);
  
  // Pickup state
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState("");
  const [pickupCustomLabel, setPickupCustomLabel] = useState(null);
  const [showPickupCalendar, setShowPickupCalendar] = useState(false);
  const [pickupTimeSlots, setPickupTimeSlots] = useState([]);
  const[confirmationModel,setConfirmationModel] = useState(false);
  const [pendingDateTimeData, setPendingDateTimeData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Initialize with current time
  useEffect(() => {
    const now = bookingWindowStart;
    
    // Set initial dropoff date to today
    setDropoffDate(now);
    
    // Generate time slots for today (from next half hour to 11:30 PM)
    const slots = generateTimeSlots(now, now, bookingWindowEnd);
    setDropoffTimeSlots(slots);
    setDropoffTime(slots[0]);
    
    // Set initial pickup (24 hours later)
    const pickupDateTime = isAfter(add24Hours(now), bookingWindowEnd)
      ? bookingWindowEnd
      : add24Hours(now);
    setPickupDate(pickupDateTime);
    
    // Generate pickup time slots (full day from 12:00 AM to 11:30 PM)
    const pickupSlots = generateTimeSlots(pickupDateTime, pickupDateTime, bookingWindowEnd);
    setPickupTimeSlots(pickupSlots);
    setPickupTime(pickupSlots[0]);
  }, [bookingWindowEnd, bookingWindowStart]);

  // Update pickup when dropoff changes
  useEffect(() => {
    if (dropoffDate && dropoffTime) {
      const dropoffDateTime = parseTimeToDate(dropoffDate, dropoffTime);
      if (dropoffDateTime) {
        const computedPickupDate = add24Hours(dropoffDateTime);
        const newPickupDate = isAfter(computedPickupDate, bookingWindowEnd)
          ? bookingWindowEnd
          : computedPickupDate;
        setPickupDate(newPickupDate);
        
        // Generate new pickup time slots (full day)
        const pickupSlotsStartTime = isSameDay(newPickupDate, dropoffDateTime)
          ? dropoffDateTime
          : newPickupDate;
        const slots = generateTimeSlots(newPickupDate, pickupSlotsStartTime, bookingWindowEnd);
        setPickupTimeSlots(slots);
        
        // Set pickup time to the same relative time if available
        const dropoffHour = getHours(dropoffDateTime);
        const dropoffMinute = getMinutes(dropoffDateTime);
        
        const matchingSlot = slots.find(slot => {
          const [time, period] = slot.split(' ');
          let [hours, minutes] = time.split(':').map(Number);
          if (period === 'PM' && hours !== 12) hours += 12;
          if (period === 'AM' && hours === 12) hours = 0;
          return hours === dropoffHour && minutes === dropoffMinute;
        });
        
        setPickupTime(matchingSlot || slots[0]);
      }
    }
  }, [bookingWindowEnd, dropoffDate, dropoffTime]);

  const dropDates = [
    { label: "Today", value: "today" },
    { label: "Tomorrow", value: "tomorrow" },
    { label: "📅 Custom", value: "custom" },
  ];

  const handleDropoffDatePill = (value) => {
    if (value === "today") {
      const today = new Date();
      setDropoffDate(today);
      setDropoffCustomLabel(null);
      setShowDropoffCalendar(false);
      
      // Generate time slots for today (from next half hour to 11:30 PM)
      const slots = generateTimeSlots(today, today, bookingWindowEnd);
      setDropoffTimeSlots(slots);
      if (slots.length > 0) {
        setDropoffTime(slots[0]);
      }
    } else if (value === "tomorrow") {
      const tomorrow = addDays(new Date(), 1);
      setDropoffDate(tomorrow);
      setDropoffCustomLabel(null);
      setShowDropoffCalendar(false);
      
      // Generate time slots for tomorrow (full day from 12:00 AM to 11:30 PM)
      const slots = generateTimeSlots(tomorrow, tomorrow, bookingWindowEnd);
      setDropoffTimeSlots(slots);
      if (slots.length > 0) {
        setDropoffTime(slots[0]); // Will be 12:00 AM
      }
    } else if (value === "custom") {
      setShowDropoffCalendar(true);
    }
  };

  const handlePickupDatePill = (value) => {
    if (value === "today") {
      const today = new Date();
      setPickupDate(today);
      setPickupCustomLabel(null);
      setShowPickupCalendar(false);
      
      // Generate time slots for today (from next half hour to 11:30 PM)
      const pickupStartTime = isSameDay(today, dropoffDate) && dropoffTime
        ? parseTimeToDate(dropoffDate, dropoffTime) || today
        : today;
      const slots = generateTimeSlots(today, pickupStartTime, bookingWindowEnd);
      setPickupTimeSlots(slots);
      if (slots.length > 0) {
        setPickupTime(slots[0]);
      }
    } else if (value === "tomorrow") {
      const tomorrow = addDays(new Date(), 1);
      setPickupDate(tomorrow);
      setPickupCustomLabel(null);
      setShowPickupCalendar(false);
      
      // Generate time slots for tomorrow (full day from 12:00 AM to 11:30 PM)
      const slots = generateTimeSlots(tomorrow, tomorrow, bookingWindowEnd);
      setPickupTimeSlots(slots);
      if (slots.length > 0) {
        setPickupTime(slots[0]); // Will be 12:00 AM
      }
    } else if (value === "custom") {
      setShowPickupCalendar(true);
    }
  };

  const handleDropoffCalendarSelect = (date, label) => {
    setDropoffDate(date);
    setDropoffCustomLabel(label);
    setShowDropoffCalendar(false);
    
    // Generate time slots for the selected date (full day from 12:00 AM to 11:30 PM)
    const slots = generateTimeSlots(date, date, bookingWindowEnd);
    setDropoffTimeSlots(slots);
    if (slots.length > 0) {
      setDropoffTime(slots[0]); // Will be 12:00 AM
    }
  };

  const handlePickupCalendarSelect = (date, label) => {
    setPickupDate(date);
    setPickupCustomLabel(label);
    setShowPickupCalendar(false);
    
    // Generate time slots for the selected date (full day from 12:00 AM to 11:30 PM)
    const pickupStartTime = isSameDay(date, dropoffDate) && dropoffTime
      ? parseTimeToDate(dropoffDate, dropoffTime) || date
      : date;
    const slots = generateTimeSlots(date, pickupStartTime, bookingWindowEnd);
    setPickupTimeSlots(slots);
    if (slots.length > 0) {
      setPickupTime(slots[0]);
    }
  };

  const handleContinue = () => {
    // Create ISO strings for reserve_start and reserve_end
    const reserveStart = formatToISOString(dropoffDate, dropoffTime);
    const reserveEnd = formatToISOString(pickupDate, pickupTime);
    
    const dateTimeData = {
      reserve_start: reserveStart,
      reserve_end: reserveEnd
    };
    
    if (onContinue) {
      onContinue(dateTimeData);
    }

    console.log("date is: ", dateTimeData);
  };

  const formatDisplayDate = (date) => {
    if (!date) return "";
    
    if (isToday(date)) {
      return `Today, ${format(date, 'M/d')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'M/d')}`;
    } else {
      return format(date, 'EEE, M/d');
    }
  };

  const dropOffDisplay = dropoffDate && dropoffTime ? `${formatDisplayDate(dropoffDate)}, ${dropoffTime}` : "Select dropoff time";
  const pickUpDisplay = pickupDate && pickupTime ? `${formatDisplayDate(pickupDate)}, ${pickupTime}` : "Select pickup time";

  const handleCancelBooking = () => {
    if (isSubmitting) {
      return;
    }

    setConfirmationModel(false);
    setPendingDateTimeData(null);
  };

  const handleConfirmBooking = async () => {
    if (!onContinue || !pendingDateTimeData || isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      const didContinueSucceed = await onContinue(pendingDateTimeData);
      console.log("Confirmed date is: ", pendingDateTimeData);

      if (didContinueSucceed !== false && isMountedRef.current) {
        setConfirmationModel(false);
        setPendingDateTimeData(null);
      }
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

   const handleContinueClick = () => {
     if (isSubmitting) {
      return;
     }

     // Create ISO strings for reserve_start and reserve_end
     const reserveStart = formatToISOString(dropoffDate, dropoffTime);
     const reserveEnd = formatToISOString(pickupDate, pickupTime);
     const reserveStartDate = reserveStart ? new Date(reserveStart) : null;
     const reserveEndDate = reserveEnd ? new Date(reserveEnd) : null;

     if (
      (reserveStartDate && isAfter(reserveStartDate, bookingWindowEnd)) ||
      (reserveEndDate && isAfter(reserveEndDate, bookingWindowEnd))
     ) {
      ShowToast(`Please select drop-off and pickup within ${MAX_BOOKING_WINDOW_MONTHS} months from today.`, TOAST_ERROR);
      return;
     }
    
    const dateTimeData = {
      reserve_start: reserveStart,
      reserve_end: reserveEnd
    };
    
    // Store the data and show modal
     setPendingDateTimeData(dateTimeData);
     setConfirmationModel(true);
   };

   const renderConfirmationContent = () => {
    const dropoffDateTime = parseTimeToDate(dropoffDate, dropoffTime);
    const pickupDateTime = parseTimeToDate(pickupDate, pickupTime);

    let durationLabel = null;
    if (dropoffDateTime && pickupDateTime) {
      const totalMinutes = differenceInMinutes(pickupDateTime, dropoffDateTime);
      if (!Number.isNaN(totalMinutes) && totalMinutes > 0) {
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
        const minutes = totalMinutes % 60;

        const parts = [];
        if (days) parts.push(`${days} ${days === 1 ? 'day' : 'days'}`);
        if (hours) parts.push(`${hours} ${hours === 1 ? 'hour' : 'hours'}`);
        if (minutes) parts.push(`${minutes} ${minutes === 1 ? 'min' : 'mins'}`);

        durationLabel = parts.join(' ');
      }
    }

    const hasInvalidRange =
      dropoffDateTime && pickupDateTime && !isAfter(pickupDateTime, dropoffDateTime);

    return (
      <Box sx={{ pt: 0.5 }}>
        <Typography variant="body2" sx={{ opacity: 0.72, mb: 2 }}>
          {t('Please confirm that these date and time selections are correct.')}
        </Typography>

        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: 1.5, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <AccessTimeRoundedIcon fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">{t('drop_off_label')}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                {dropOffDisplay}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box sx={{ p: 1.5, display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
            <EventAvailableRoundedIcon fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2">{t('pick_up_label')}</Typography>
              <Typography variant="body2" sx={{ opacity: 0.72 }}>
                {pickUpDisplay}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <div className="date-time-selector">
      <div className="phone-shell">
        {/* Scrollable Content */}
        <div className="scroll-area">
          <p className="description">
            {/* {t('date_time_selector_description')} */}
          </p>

          {/* Summary Card */}
          <div className="summary-card">
            <div 
              className={`summary-row ${activeSection === "dropoff" ? "active" : ""}`} 
              onClick={() => setActiveSection("dropoff")}
            >
              <div className="summary-left">
                <div className={`dot ${activeSection === "dropoff" ? "active" : ""}`} />
                <span className="summary-label">{t('drop_off_label')}</span>
              </div>
              <span className="summary-value">{dropOffDisplay}</span>
            </div>
            
            <div 
              className={`summary-row ${activeSection === "pickup" ? "active" : ""}`} 
              onClick={() => setActiveSection("pickup")}
            >
              <div className="summary-left">
                <div className={`dot ${activeSection === "pickup" ? "active" : ""}`} />
                <span className="summary-label">{t('pick_up_label')}</span>
              </div>
              <span className="summary-value">{pickUpDisplay}</span>
            </div>
          </div>

          {/* Dropoff Section */}
          {activeSection === "dropoff" && (
            <>
              <div className="section-label">{t('dropoff_date_label')}</div>
              <div className="date-row">
                {dropDates.map((d) => {
                  let isSelected = false;
                  
                  if (d.value === "today") {
                    isSelected = isToday(dropoffDate);
                  } else if (d.value === "tomorrow") {
                    isSelected = isTomorrow(dropoffDate);
                  } else if (d.value === "custom") {
                    isSelected = dropoffCustomLabel !== null && !isToday(dropoffDate) && !isTomorrow(dropoffDate);
                  }
                  
                  return (
                    <div
                      key={d.value}
                      className={`date-pill ${isSelected ? "selected" : ""}`}
                      onClick={() => handleDropoffDatePill(d.value)}
                    >
                      <div className="date-pill-label">
                        {d.value === "custom" && dropoffCustomLabel 
                          ? dropoffCustomLabel
                          : d.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Dropoff Calendar */}
              {showDropoffCalendar && (
                <Calendar
                  onSelect={handleDropoffCalendarSelect}
                  onClose={() => setShowDropoffCalendar(false)}
                  minDate={bookingWindowStart}
                  maxDate={bookingWindowEnd}
                  selectedDate={dropoffDate}
                />
              )}

              {/* Dropoff Time Selection */}
              <div className="section-label">
                {t('dropoff_time_label')}
              </div>
              <div className="time-list-container">
                <div className="time-list">
                  {dropoffTimeSlots.map((t) => (
                    <button
                      key={t}
                      className={`time-slot ${dropoffTime === t ? "selected" : ""}`}
                      onClick={() => setDropoffTime(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Pickup Section */}
          {activeSection === "pickup" && (
            <>
              <div className="section-label">{t('pickup_date_label')}</div>
              <div className="date-row">
                {dropDates.map((d) => {
                  let isSelected = false;
                  
                  if (d.value === "today") {
                    isSelected = isToday(pickupDate);
                  } else if (d.value === "tomorrow") {
                    isSelected = isTomorrow(pickupDate);
                  } else if (d.value === "custom") {
                    isSelected = pickupCustomLabel !== null && !isToday(pickupDate) && !isTomorrow(pickupDate);
                  }
                  
                  return (
                    <div
                      key={d.value}
                      className={`date-pill ${isSelected ? "selected" : ""}`}
                      onClick={() => handlePickupDatePill(d.value)}
                    >
                      <div className="date-pill-label">
                        {d.value === "custom" && pickupCustomLabel 
                          ? pickupCustomLabel
                          : d.label}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Pickup Calendar */}
              {showPickupCalendar && (
                <Calendar
                  onSelect={handlePickupCalendarSelect}
                  onClose={() => setShowPickupCalendar(false)}
                  minDate={bookingWindowStart}
                  maxDate={bookingWindowEnd}
                  selectedDate={pickupDate}
                />
              )}

              {/* Pickup Time Selection */}
              <div className="section-label">
                {t('pickup_time_label')}
              </div>
              <div className="time-list-container">
                <div className="time-list">
                  {pickupTimeSlots.map((t) => (
                    <button
                      key={t}
                      className={`time-slot ${pickupTime === t ? "selected" : ""}`}
                      onClick={() => setPickupTime(t)}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom Area */}
        <div className="bottom-area">
          <button 
            className="quick-select-btn"
          >
            {dropOffDisplay} → {pickUpDisplay}
          </button>
          <button 
            className="continue-btn"
            onClick={handleContinueClick}
            disabled={isSubmitting}
          >
            {t('button_continue')}
          </button>
        </div>
      </div>
      
    {confirmationModel && (
      <CustomDialog
        dialogId='confirmation-dialog'
        dialogVisible={confirmationModel}
        dialogTitle={t('Please confirm your booking Date and Time')}
        buttonOneTitle={t('button_cancel')}
        buttonTwoTitle={t('button_ok')}
        buttonProps={{
          buttonOne: { type: BUTTON_WARNING },
          buttonTwo: { type: BUTTON_PRIMARY }
        }}
        handleAccept={handleConfirmBooking}
        handleCancel={handleCancelBooking}
        onClose={handleCancelBooking}
      >
        {renderConfirmationContent}
      </CustomDialog>
)}
    </div>
  );
};

export default DateTimeSelector;
